import { useMemo, useState, type ReactElement } from "react";
import { Header, Card } from "../molecules";
import {
  SearchOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  SwapOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import "../../resources/transactions.scss";
import { setTotals, transactionState } from "../../store/slices/transaction";
import { useDispatch } from "react-redux";
import {
  TransactionType,
  TransactionTypeEnum,
} from "../../constants/transaction";
import { useAppSelector } from "../../app/hooks";

const typeLabels: Record<TransactionTypeEnum | "all", string> = {
  all: "All",
  Income: TransactionTypeEnum.INCOME,
  Expense: TransactionTypeEnum.EXPENSE,
  Transfer: TransactionTypeEnum.TRANSFER,
};

const typeIcons: Record<TransactionTypeEnum, ReactElement> = {
  Income: <ArrowUpOutlined />,
  Expense: <ArrowDownOutlined />,
  Transfer: <SwapOutlined />,
};

export const Transactions = () => {
  const dispatch = useDispatch();
  const { transactions, income, expense, transfer } =
    useAppSelector(transactionState);

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedType, setSelectedType] = useState<"all" | TransactionTypeEnum>(
    "all",
  );

  const filteredTransactions = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    return transactions.filter((transaction: TransactionType) => {
      const matchesType =
        selectedType === "all" || transaction.type === selectedType;
      const matchesSearch =
        normalizedSearch === "" ||
        transaction.description.toLowerCase().includes(normalizedSearch) ||
        transaction.category.toLowerCase().includes(normalizedSearch) ||
        transaction._id.toLowerCase().includes(normalizedSearch) ||
        transaction.date.includes(normalizedSearch) ||
        transaction.amount.toString().includes(normalizedSearch);

      return matchesType && matchesSearch;
    });
  }, [searchTerm, selectedType, transactions]);

  useMemo(() => {
    const totals: any = {
      income: transactions
        .filter((item) => item.type === TransactionTypeEnum.INCOME)
        .reduce((sum, item) => sum + item.amount, 0),
      expense: transactions
        .filter((item) => item.type === TransactionTypeEnum.EXPENSE)
        .reduce((sum, item) => sum + item.amount, 0),
      transfer: transactions
        .filter((item) => item.type === TransactionTypeEnum.TRANSFER)
        .reduce((sum, item) => sum + item.amount, 0),
    };

    dispatch(setTotals({ ...totals }));
  }, [transactions, dispatch]);

  const handleExportCSV = () => {
    const headers = ["ID", "Date", "Description", "Category", "Type", "Amount"];
    const rows = filteredTransactions.map((transaction) => [
      transaction._id,
      transaction.date,
      transaction.description,
      transaction.category,
      typeLabels[transaction.type],
      transaction.amount.toFixed(2),
    ]);
    const csvContent = [headers, ...rows]
      .map((row) => row.map((value) => `"${value}"`).join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute(
      "download",
      `transactions-${new Date().toISOString().slice(0, 10)}.csv`,
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="cp-transactions">
      <Header />

      <div className="cp-transactions__container">
        <section className="cp-transactions__hero">
          <div>
            <h2>All Transactions</h2>
            <p>
              Search, filter and review every payment, transfer and income
              record.
            </p>
          </div>
          <div className="cp-transactions__stats">
            <div>
              <span>Income</span>
              <strong>${income.toFixed(2)}</strong>
            </div>
            <div>
              <span>Expenses</span>
              <strong>${Math.abs(expense).toFixed(2)}</strong>
            </div>
            <div>
              <span>Transfers</span>
              <strong>${Math.abs(transfer).toFixed(2)}</strong>
            </div>
          </div>
        </section>

        <Card>
          <div className="cp-transactions__toolbar">
            <div className="cp-transactions__search">
              <SearchOutlined />
              <input
                type="search"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Search by vendor, category, amount or date"
              />
            </div>

            <div className="cp-transactions__toolbar-actions">
              <div className="cp-transactions__filters">
                {Object.keys(typeLabels).map((type) => (
                  <button
                    key={type}
                    type="button"
                    className={`cp-transactions__filter-btn ${selectedType === type ? "cp-transactions__filter-btn--active" : ""}`}
                    onClick={() =>
                      setSelectedType(type as "all" | TransactionTypeEnum)
                    }
                  >
                    {typeLabels[type as "all" | TransactionTypeEnum]}
                  </button>
                ))}
              </div>
              <button
                type="button"
                className="cp-transactions__export-btn"
                onClick={handleExportCSV}
              >
                <DownloadOutlined /> Download CSV
              </button>
            </div>
          </div>

          <div className="cp-transactions__table">
            <ul>
              {filteredTransactions.length === 0 ? (
                <li className="cp-transactions__empty">
                  No transactions match your search. Try adjusting the filter or
                  search text.
                </li>
              ) : (
                filteredTransactions.map((transaction) => (
                  <li key={transaction._id}>
                    <div className="cp-transactions__row">
                      <div className="cp-transactions__icon">
                        {typeIcons[transaction.type]}
                      </div>
                      <div className="cp-transactions__details">
                        <p>{transaction.description}</p>
                        <span>{transaction.category}</span>
                      </div>
                    </div>

                    <span>{transaction.date}</span>
                    <span
                      className={`cp-transactions__amount cp-transactions__amount--${transaction.type}`}
                    >
                      {transaction.type === TransactionTypeEnum.EXPENSE
                        ? "-"
                        : "+"}
                      ${Math.abs(transaction.amount).toFixed(2)}
                    </span>
                    <span className="cp-transactions__badge">
                      {typeLabels[transaction.type]}
                    </span>
                  </li>
                ))
              )}
            </ul>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default Transactions;
